import {isFeatureEnabled} from '@github-ui/feature-flags'
import {useShouldShowCopilotFunctionalityInIssues} from '@github-ui/issue-create/hooks/use-should-show-copilot-functionality-in-issues'
import {MAX_QUERIES_TO_MARK_FOR_PREHEATING} from '@github-ui/relay-query-cache/contexts/RelayCachePreheatContext'
import {ssrSafeLocation} from '@github-ui/ssr-utils'
import {getHighlightedEvent} from '@github-ui/timeline-items/HighlightedEvent'
import {LABELS} from '@github-ui/timeline-items/Labels'
import {VALUES} from '@github-ui/timeline-items/Values'
import {Timeline} from '@primer/react'
import {type ReactNode, useEffect, useMemo, useRef, useState} from 'react'
import {graphql, useFragment} from 'react-relay'

import {TEST_IDS} from '../../constants/test-ids'
import {useCachePreheatIssueViewerQueries} from '../../hooks/use-cache-preheat-issueviewer'
import {useSafariTimelineScrollObserver} from '../../hooks/use-safari-scroll-preservation'
import {useNewScrollToHighlighted} from '../../hooks/use-scroll-to-highlighted'
import {gatherIssueLinks} from '../../utils/helpers'
import type {RolledUpTimelineItem} from '../../utils/timeline-rollups'
import {parseGitHubIssueUrl} from '../../utils/urls'
import type {IssueViewerIssue$data} from '../__generated__/IssueViewerIssue.graphql'
import {useIssueViewerSubscription} from '../IssueViewerSubscription'
import type {IssueTimelineIssueFragment$key} from './__generated__/IssueTimelineIssueFragment.graphql'
import type {IssueTimelineItem$data} from './__generated__/IssueTimelineItem.graphql'
import type {IssueTimelineSecondary$key} from './__generated__/IssueTimelineSecondary.graphql'
import {CrawlerPagination} from './CrawlerPagination'
import type {EventProps} from './IssueTimelineItem'
import type {LoadMoreCallbackFn} from './LoadMore'
import {TimelineTransferringFlash} from './TimelineTransferringFlash'
import {isEventHighlighted} from './use-timeline-highlight'
import {useTimelineItems} from './use-timeline-items'
import {VirtualIssueTimelinePage} from './VirtualIssueTimelinePage'

type LoadItem = {
  type: 'load'
  position: 'top' | 'bottom'
  loadFromTop: LoadMoreCallbackFn
  loadFromBottom: LoadMoreCallbackFn
  numberOfRemainingItems: number
  isCurrentlyLoading?: boolean
}

type EventItem = {type: 'event'} & RolledUpTimelineItem<IssueTimelineItem$data>
export type TimelineItem = EventItem | LoadItem

/**
 * Temporary type for parsing the timeline items and grouping them into section elements
 * for improve the accessibility of the event items by adding landmarks for navigation.
 */
export type RenderItem = {
  /**
   * Controls whether the item has already been added to the grouped events or not.
   *
   * Only used in events and not in load items, graceful degratation items or issue comments.
   */
  isAddedToGroupedEvents: boolean
  timelineItem: TimelineItem
  render: ReactNode
}

// eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-queries
const mainTimelineFragment = graphql`
  fragment IssueTimelineIssueFragment on Issue
  @argumentDefinitions(skip: {type: "Int"}, count: {type: "Int", defaultValue: 15}) {
    id
    url
    repository {
      id
      nameWithOwner
      visibility
      viewerCanPush
    }
    ...useTimelineItemsFrontFragment @arguments(count: $count, skip: $skip)
    ...useTimelineItemsBackFragment
  }
`

type IssueTimelineLiveUpdatesLoaderProps = {
  issueId: string
  itemCount: number
}
const IssueTimelineLiveUpdatesLoader = ({issueId, itemCount}: IssueTimelineLiveUpdatesLoaderProps) => {
  useIssueViewerSubscription(issueId, itemCount, 'Issue__backTimelineItems')
  return null
}

type IssueTimelineProps = {
  issue: IssueViewerIssue$data
  issueSecondary: IssueTimelineSecondary$key | undefined
  highlightedEvent: string | undefined
  page?: number
} & EventProps
export const IssueTimeline = ({
  issue,
  issueSecondary,
  viewer,
  highlightedEvent,
  page,
  onCommentChange,
  onCommentReply,
  onCommentEditCancel,
  optionConfig,
}: IssueTimelineProps) => {
  const [issueLinks, setIssueLinks] = useState<string[]>([])
  const data = useFragment<IssueTimelineIssueFragment$key>(mainTimelineFragment, issue)
  const secondaryData = useFragment(
    // eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-queries
    graphql`
      fragment IssueTimelineSecondary on Issue {
        isTransferInProgress
      }
    `,
    issueSecondary,
  )

  const showCopilotMenuItem = useShouldShowCopilotFunctionalityInIssues({
    repositoryVisibility: data.repository.visibility,
    userCanPushToRepo: data.repository.viewerCanPush,
  })

  const highlight = useMemo(() => getHighlightedEvent(highlightedEvent), [highlightedEvent])

  // This state controls if the highlight borders should be shown or not
  const [shouldHighlightElement, setShouldHighlightElement] = useState<boolean>(highlight !== undefined)

  // After rendering, clicking anywhere should remove the borders
  useEffect(() => {
    const handlePageClick = () => {
      setShouldHighlightElement(false)
    }
    document.addEventListener('click', handlePageClick)

    return () => {
      document.removeEventListener('click', handlePageClick)
    }
  }, [highlight])

  // If a new element's link is clicked, we should restore the highlight
  useEffect(() => {
    if (!highlight?.id) return

    // eslint-disable-next-line react-hooks/set-state-in-effect
    setShouldHighlightElement(true)
  }, [highlight?.id])

  const timelinePaginationForBotsEnabled = isFeatureEnabled('issues_react_bots_timeline_pagination')

  const {timelineItems, totalItemCount, highlightedReady} = useTimelineItems({
    timelineData: data,
    highlightedEvent,
    page: timelinePaginationForBotsEnabled ? page : undefined,
  })

  const hasNextPage = timelinePaginationForBotsEnabled && getHasNextPage(totalItemCount, page)

  // Ref for the timeline container to gather issue links on demand
  const timelineContainerRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const links = gatherIssueLinks(timelineContainerRef.current)
    setIssueLinks(links)
  }, [totalItemCount])

  const issuesWithLinks: Array<{owner: string; repo: string; number: number}> = useMemo(() => {
    const items: Array<{owner: string; repo: string; number: number}> = []
    for (const link of issueLinks) {
      const parsed = parseGitHubIssueUrl(link)
      if (parsed) {
        items.push(parsed)
        if (items.length >= MAX_QUERIES_TO_MARK_FOR_PREHEATING) {
          break
        }
      }
    }
    return items
  }, [issueLinks])

  useCachePreheatIssueViewerQueries({
    issues: issuesWithLinks,
    source: 'IssueTimeline',
  })

  // Iterate items to check if the loaded highlight is in the list
  // Room for performance optimization if we face issues in large timelines, as we can determine
  // if the highlighted item is already present during the mapping process.
  // https://github.com/github/github/blob/9ecd32f60f96a519f99462c48f6235d5842ca814/ui/packages/issue-viewer/components/timeline/IssueTimeline.tsx#L249
  const highlightedItemRef = useRef<HTMLDivElement>(null)
  const timelineRef = useRef<HTMLDivElement>(null)

  const isHighlightLoaded = useMemo(() => {
    if (!timelineItems) return false
    if (!shouldHighlightElement || !highlight) return false

    return timelineItems.some(item => {
      if (item.type !== 'event' || !item.item) return false

      return isEventHighlighted(item.item, highlight)
    })
  }, [highlight, timelineItems, shouldHighlightElement])

  useNewScrollToHighlighted(isHighlightLoaded, highlightedItemRef, highlightedEvent, highlightedReady)

  // Safari doesn't support CSS scroll anchoring, so we need to manually observe
  // DOM changes and preserve scroll position when timeline items are added/removed
  useSafariTimelineScrollObserver(timelineRef)

  if (!timelineItems) {
    throw new Error(
      `Missing timeline items for issue ${issue.id}.  Total item count: ${totalItemCount}.  Highlighted event: ${highlightedEvent}`,
    )
  }

  const paginatedTimelineItems = useMemo(() => {
    const pageSize = VALUES.timeline.virtualPageSize
    const pages = []
    if (timelineItems.length === 0) {
      return []
    }
    const getTimelineItemCount = (item: TimelineItem) => {
      // undefined items or load items are not counted
      if (!item || item.type !== 'event') {
        return 0 // Load items count as 1
      }
      if (!item.rollupGroup) {
        return 1 // Single event item
      }
      // If the item has a rollup group, count the number of rolled up events for each rollup type
      return Object.values(item.rollupGroup).reduce((count, rollup) => count + rollup.length, 0)
    }

    let currentPage: TimelineItem[] = []
    let currentPageItemCount = 0

    for (const timelineItem of timelineItems) {
      const itemCount = getTimelineItemCount(timelineItem)

      // Check if adding this item would exceed the page size
      if (currentPageItemCount + itemCount > pageSize && currentPage.length > 0) {
        // Add current page to pages and start a new page
        pages.push(currentPage)
        currentPage = [timelineItem]
        currentPageItemCount = itemCount
      } else {
        // Add item to current page
        currentPage.push(timelineItem)
        currentPageItemCount += itemCount
      }
    }

    // Add the last page if it has items
    if (currentPage.length > 0) {
      pages.push(currentPage)
    }

    return pages
  }, [timelineItems])

  return (
    <div ref={timelineContainerRef}>
      {Boolean(viewer) && <IssueTimelineLiveUpdatesLoader issueId={issue.id} itemCount={totalItemCount} />}
      {secondaryData?.isTransferInProgress && <TimelineTransferringFlash />}
      <h2 className="sr-only">{LABELS.timeline.header}</h2>
      {hasNextPage && <CrawlerPagination nextPage={page ? page + 1 : 1} canonicalUrl={ssrSafeLocation.pathname} />}
      <Timeline ref={timelineRef} id="issue-timeline" data-testid={TEST_IDS.issueTimelineContainer}>
        {paginatedTimelineItems.map(pageItems => (
          <VirtualIssueTimelinePage
            key={`page-${pageItems.length}-${
              pageItems[0]?.type === 'event' ? `event-${pageItems[0]?.item?.__id}` : `load`
            }`}
            timelineItems={pageItems}
            shouldHighlightElement={shouldHighlightElement}
            highlight={highlight}
            viewer={viewer}
            issueId={data.id}
            repositoryId={data.repository.id}
            repositoryNameWithOwner={data.repository.nameWithOwner}
            issueUrl={data.url}
            onCommentChange={onCommentChange}
            onCommentReply={onCommentReply}
            onCommentEditCancel={onCommentEditCancel}
            optionConfig={optionConfig}
            highlightedItemRef={highlightedItemRef}
            showCopilotMenuItem={showCopilotMenuItem}
          />
        ))}
      </Timeline>
    </div>
  )
}

function getHasNextPage(totalItemCount: number, page?: number): boolean {
  if (!page) {
    // The initial load returns 30 items (15 from front and 15 from back)
    return totalItemCount > 30
  }

  // 150 is the page size (same size we use when a regular user clicks "Load more")
  return totalItemCount > 15 + page * 150
}

try{ IssueTimelineLiveUpdatesLoader.displayName ||= 'IssueTimelineLiveUpdatesLoader' } catch {}
try{ IssueTimeline.displayName ||= 'IssueTimeline' } catch {}